/* USERS PAGE - Fixed Crash and Atomic Provisioning */
import React, { useState } from 'react';
import { useStore } from '../store/StoreContext';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { Button } from '../components/UI/Button';
import { Modal } from '../components/UI/Modal';
import { Input, Select } from '../components/UI/Input';
import supabase from '../supabase/supabase';
import { generateId } from '../utils/helpers';

const ROLE_OPTIONS = {
  master_admin: [
    { label: 'Admin Enterprise (Unlimited)', value: 'admin_enterprise' },
    { label: 'Admin Gold (20 Proj/5 BU)', value: 'admin_gold' },
    { label: 'Admin Bronze (5 Proj/1 BU)', value: 'admin_bronze' },
    { label: 'Member', value: 'member' }
  ],
  admin: [
    { label: 'Member', value: 'member' }
  ]
};

const UserAvatar = ({ user }) => {
  const initials = user.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)
    : '??';

  return (
    <div className="w-10 h-10 rounded-xl bg-base-900 border border-base-700 flex items-center justify-center overflow-hidden shrink-0 shadow-inner group-hover:border-primary/50 transition-colors">
      {user.avatar ? (
        <img src={user.avatar} className="w-full h-full object-cover" alt={user.name} />
      ) : (
        <span className="text-xs font-black text-primary tracking-tighter">{initials}</span>
      )}
    </div>
  );
};

export const UsersPage = () => {
  const { state, refresh } = useStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [provisionSuccess, setProvisionSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'member',
    name: '',
    canCreateProjects: false,
    maxBus: 1,
    maxProjectsPerBu: 5
  });

  const currentUser = state.currentUser;
  const isMaster = currentUser?.role === 'master_admin';
  const roleOptions = isMaster ? ROLE_OPTIONS.master_admin : ROLE_OPTIONS.admin;

  // Safe user list calculation
  const users = Object.values(state.users || {}).filter(u => u && u.id !== currentUser?.id);

  const resetForm = () => {
    setFormData({ email: '', password: '', role: 'member', name: '', canCreateProjects: false, maxBus: 1, maxProjectsPerBu: 5 });
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      const isNewAdmin = formData.role.startsWith('admin');
      
      // 1. Create the Auth User
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        options: {
          data: {
            name: formData.name,
            role: formData.role,
            parent_id: currentUser.id
          }
        }
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error("Account creation failed. Email might be in use.");
      
      const newUserId = authData.user.id;

      // 2. ATOMIC Profile Creation
      const { error: profileError } = await supabase.from('users_2024').insert({
        id: newUserId,
        email: formData.email.trim().toLowerCase(),
        name: formData.name,
        role: formData.role,
        parent_id: currentUser.id,
        permissions: { canCreateProjects: isNewAdmin }
      });

      if (profileError) throw profileError;

      // 3. Workspace Provisioning for Admins
      if (isNewAdmin) {
        const wsId = generateId('ws');
        const { error: wsError } = await supabase.from('workspaces_2024').insert({
          id: wsId,
          name: `${formData.name}'s Workspace`,
          owner_id: newUserId,
          max_bus: formData.maxBus,
          max_projects_per_bu: formData.maxProjectsPerBu,
          max_members: 10
        });

        if (wsError) throw wsError;

        await supabase.from('workspace_members_2024').insert({
          id: generateId('wm'),
          workspace_id: wsId,
          user_id: newUserId,
          role: 'admin'
        });
      } else {
        // Regular members join the current admin's workspace
        await supabase.from('workspace_members_2024').insert({
          id: generateId('wm'),
          workspace_id: state.currentWorkspace.id,
          user_id: newUserId,
          role: 'user'
        });
      }

      setProvisionSuccess(true);
      resetForm();
      await refresh();
    } catch (err) {
      console.error("Provisioning Error:", err);
      alert(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateAccount = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      await supabase.from('users_2024').update({
        name: formData.name,
        permissions: { ...targetUser?.permissions, canCreateProjects: formData.canCreateProjects }
      }).eq('id', targetUser?.id);

      if (targetUser?.role?.startsWith('admin')) {
        await supabase.from('workspaces_2024').update({
          max_bus: formData.maxBus,
          max_projects_per_bu: formData.maxProjectsPerBu
        }).eq('owner_id', targetUser?.id);
      }

      await refresh();
      setIsEditModalOpen(false);
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const openEdit = async (user) => {
    setTargetUser(user);
    setIsEditModalOpen(true); // Open modal immediately for better UX
    
    let limits = { max_bus: 1, max_projects_per_bu: 5 };
    
    if (user.role?.startsWith('admin')) {
      const { data } = await supabase.from('workspaces_2024').select('*').eq('owner_id', user.id).maybeSingle();
      if (data) limits = data;
    }

    setFormData({
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'member',
      canCreateProjects: user.permissions?.canCreateProjects || false,
      maxBus: limits.max_bus,
      maxProjectsPerBu: limits.max_projects_per_bu
    });
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-8 h-full overflow-y-auto bg-base-900">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Access Control</h1>
          <p className="text-sm text-gray-500 font-medium">Provision accounts and manage workspace quotas.</p>
        </div>
        <Button onClick={() => { setProvisionSuccess(false); resetForm(); setIsModalOpen(true); }}>
          <SafeIcon name="UserPlus" className="mr-2" /> Provision Account
        </Button>
      </div>

      <div className="bg-base-800 rounded-2xl border border-base-700 overflow-hidden shadow-2xl">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-base-900/50 border-b border-base-700">
              <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest pl-6">User Identity</th>
              <th className="p-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Tier</th>
              <th className="p-4 text-right pr-6"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-base-700">
            {users.map(u => (
              <tr key={u.id} className="hover:bg-base-700/30 transition-colors group">
                <td className="p-4 pl-6">
                  <div className="flex items-center gap-4">
                    <UserAvatar user={u} />
                    <div className="min-w-0">
                      <div className="font-bold text-white truncate">{u.name}</div>
                      <div className="text-[11px] text-gray-500 font-mono tracking-tighter truncate">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded text-[9px] font-black uppercase border ${u.role?.startsWith('admin') ? 'border-primary/40 text-primary bg-primary/5' : 'border-base-600 text-gray-500'}`}>
                    {(u.role || 'member').replace('_', ' ')}
                  </span>
                </td>
                <td className="p-4 text-right pr-6">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => openEdit(u)} className="p-2 text-gray-400 hover:text-white bg-base-900 border border-base-700 rounded-lg">
                      <SafeIcon name="Edit2" className="w-4 h-4" />
                    </button>
                    <button onClick={async () => { if(confirm('Revoke access?')) { await supabase.from('users_2024').delete().eq('id', u.id); refresh(); } }} className="p-2 text-gray-500 hover:text-red-500 bg-base-900 border border-base-700 rounded-lg">
                      <SafeIcon name="Trash2" className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan="3" className="p-12 text-center text-gray-500 italic text-sm">No subordinate accounts found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Provision Account">
        {provisionSuccess ? (
          <div className="text-center py-8">
            <SafeIcon name="CheckCircle" className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white">Provisioned</h2>
            <p className="text-gray-400 text-sm mt-2 mb-6">Account and workspace are live.</p>
            <Button onClick={() => setIsModalOpen(false)} className="w-full">Done</Button>
          </div>
        ) : (
          <form onSubmit={handleCreateAccount} className="space-y-4">
            <Input label="Full Name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
            <Input label="Email" type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required />
            <Input label="Temp Password" type="password" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} required />
            <Select label="Role Tier" value={formData.role} onChange={e => setFormData({ ...formData, role: e.target.value })} options={roleOptions} />
            
            {formData.role.startsWith('admin') && (
              <div className="p-4 bg-base-900 rounded-xl border border-base-700 space-y-4 mt-4">
                <div className="text-[10px] font-black text-primary uppercase tracking-widest">Initial Workspace Quotas</div>
                <div className="grid grid-cols-2 gap-4">
                  <Input label="Max BUs" type="number" value={formData.maxBus} onChange={e => setFormData({ ...formData, maxBus: parseInt(e.target.value) })} />
                  <Input label="Projects per BU" type="number" value={formData.maxProjectsPerBu} onChange={e => setFormData({ ...formData, maxProjectsPerBu: parseInt(e.target.value) })} />
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-6 border-t border-base-700">
              <Button variant="ghost" type="button" onClick={() => setIsModalOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={processing}>Deploy Account</Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Manage Account">
        <form onSubmit={handleUpdateAccount} className="space-y-6">
          <Input label="Display Name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
          {targetUser?.role?.startsWith('admin') ? (
            <div className="space-y-4 p-4 bg-base-900 rounded-xl border border-base-700">
              <div className="text-[10px] font-black text-primary uppercase tracking-widest mb-2">Workspace Quotas</div>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Max Business Units" type="number" value={formData.maxBus} onChange={e => setFormData({ ...formData, maxBus: parseInt(e.target.value) })} />
                <Input label="Projects per Unit" type="number" value={formData.maxProjectsPerBu} onChange={e => setFormData({ ...formData, maxProjectsPerBu: parseInt(e.target.value) })} />
              </div>
            </div>
          ) : (
            <div className="p-4 bg-base-900 rounded-xl border border-base-700 flex items-center justify-between">
              <span className="text-xs font-bold text-gray-400">Grant Project Creation Privilege</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" checked={formData.canCreateProjects} onChange={e => setFormData({ ...formData, canCreateProjects: e.target.checked })} />
                <div className="w-11 h-6 bg-base-700 rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
              </label>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-6 border-t border-base-700">
            <Button variant="ghost" type="button" onClick={() => setIsEditModalOpen(false)}>Discard</Button>
            <Button type="submit" disabled={processing}>Update Record</Button>
          </div>
        </form>
      </Modal>
    </motion.div>
  );
};